import { assertHeights, cloneHeights, inBounds, SIZE } from './board.js';
import { resolveConfig, type RulesConfig } from './config.js';
import { RuleError } from './errors.js';
import { positionKey } from './hash.js';
import type { BoardOrigin, GameState, Piece, PieceType, SetupPiece, Team } from './types.js';

export type { SetupPiece } from './types.js';

/** Back row from c = 0 to 8. */
export const BACK_ROW: readonly (PieceType | null)[] = [
  null,
  'X',
  'C',
  'L',
  'B',
  'M',
  'C',
  'X',
  null,
];

/** Basalt's back row in the experimental `corners` layout: the Beacon starts on a9. */
export const CORNER_BACK_ROW: readonly (PieceType | null)[] = [
  'B',
  'X',
  'C',
  'L',
  null,
  'M',
  'C',
  'X',
  null,
];

/**
 * Starting pieces: Basalt on r = 0, Ivory on r = 8. In the standard layout both rows read the same
 * from a to i. In the `corners` layout Ivory's row is the point mirror of Basalt's, so Ivory's
 * Beacon starts on i1.
 */
export function defaultSetup(
  layout: 'standard' | 'corners' = 'standard',
  withLookout = true,
): SetupPiece[] {
  const pieces: SetupPiece[] = [];
  const row = (layout === 'corners' ? CORNER_BACK_ROW : BACK_ROW).map((type) =>
    type === 'L' && !withLookout ? null : type,
  );
  row.forEach((type, c) => {
    if (type !== null) pieces.push({ team: 'B', type, r: 0, c });
  });
  row.forEach((_, c) => {
    // Ivory reads its row from the same side (standard) or from the opposite side (corners).
    const type = layout === 'corners' ? row[SIZE - 1 - c] : row[c];
    if (type) pieces.push({ team: 'I', type, r: SIZE - 1, c });
  });
  return pieces;
}

export interface NewGameOptions {
  config?: Partial<RulesConfig>;
  /** Where the heights came from. Defaults to a custom board holding a copy of `heights`. */
  board?: BoardOrigin;
  /** Team to move first. Ivory by default; other values are for puzzles and tests. */
  turn?: Team;
}

export function newGame(
  heights: number[][],
  setup?: SetupPiece[],
  options: NewGameOptions = {},
): GameState {
  const config = resolveConfig(options.config);
  assertHeights(heights, config.maxHeight);
  // With the opening-build compensation, Basalt makes the first action.
  const firstTurn: Team = config.secondPlayerCompensation === 'opening-build' ? 'B' : 'I';
  const turn = options.turn ?? firstTurn;
  const pieces = buildPieces(setup ?? defaultSetup(config.startLayout, config.lookout));
  const state: GameState = {
    heights: cloneHeights(heights),
    pieces,
    turn,
    ply: 0,
    moveNo: 1,
    winner: null,
    winReason: null,
    log: [],
    config,
    board: options.board ?? { kind: 'custom', heights: cloneHeights(heights) },
    start:
      setup === undefined && turn === firstTurn
        ? null
        : {
            setup: (setup ?? defaultSetup(config.startLayout, config.lookout)).map((p) => ({
              ...p,
            })),
            turn,
          },
    positions: {},
    summitStreak: { I: 0, B: 0 },
    memory: config.fog ? { I: cloneHeights(heights), B: cloneHeights(heights) } : null,
  };
  state.positions[positionKey(state)] = 1;
  return state;
}

function buildPieces(setup: SetupPiece[]): Piece[] {
  const totals = new Map<string, number>();
  for (const p of setup) totals.set(p.team + p.type, (totals.get(p.team + p.type) ?? 0) + 1);

  const counters = new Map<string, number>();
  const ids = new Set<string>();
  const squares = new Set<number>();
  const pieces: Piece[] = [];

  for (const p of setup) {
    if (!inBounds(p.r, p.c))
      throw new RuleError('ILLEGAL_ACTION', `Piece off the board at (${p.r}, ${p.c})`);
    const key = p.team + p.type;
    const n = (counters.get(key) ?? 0) + 1;
    counters.set(key, n);
    const id = p.id ?? ((totals.get(key) ?? 0) > 1 ? `${key}${n}` : key);
    if (ids.has(id)) throw new RuleError('ILLEGAL_ACTION', `Duplicate piece id ${id}`);
    if (squares.has(p.r * SIZE + p.c)) {
      throw new RuleError('ILLEGAL_ACTION', `Two pieces on (${p.r}, ${p.c})`);
    }
    ids.add(id);
    squares.add(p.r * SIZE + p.c);
    pieces.push({ id, team: p.team, type: p.type, r: p.r, c: p.c, alive: true });
  }

  for (const team of ['I', 'B'] as const) {
    if ((totals.get(`${team}B`) ?? 0) > 1) {
      throw new RuleError('ILLEGAL_ACTION', `${team} has more than one Beacon`);
    }
  }
  return pieces;
}
